import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, ModulePicker, PriceConfirmDialog, ShotMixPicker, StudioPanel, SubmitBar } from '@/components/studio'
import {
  Card,
  CardBody,
  CardHead,
  CountInput,
  Field,
  GroupedSelect,
  Input,
  Segmented,
  Select,
  Textarea,
} from '@/components/ui'
import { RefUploader } from '@/components/RefUploader'
import { ModelSelect } from '@/components/ModelSelect'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { BEAT_NAME } from '@/config/detailPlan'
import { Icon } from '@/components/icons'
import { DEFAULT_PLATFORM_ID, PLATFORM_GROUPS, getPlatform } from '@/config/platforms'
import {
  detailSizes,
  mobilePairs,
  sizeLabel,
  slotCount,
  type DetailSpec,
} from '@/config/platformDetails'
import {
  DEFAULT_MODE,
  STUDIO_MODES,
  getMode,
  type StudioModeId,
} from '@/config/studioModes'
/*
 * 形态层与提示词层都**不住在本页**（2026-09-29 上提）：
 *   `config/studioShape.ts`  —— 张数选项、档位、形态默认值、尺寸写法
 *   `lib/detailPrompts.ts`   —— 产品信息 → 提示词（全站唯一一份组装逻辑）
 * 批量工作台（`pages/BatchStudio.tsx`）与这一页必须用同一份 ——
 * 复制一份出来的话，`smoke-prompts` 守得住这一页、守不住那一页。
 */
import {
  MAX_BATCH_COUNT,
  clampCount,
  countOf,
  defaultMainMix,
  defaultsFor,
  flatChoices,
  mixTotal,
  moduleCountsOf,
  moduleShotsTotal,
  normalizeMix,
  ratioChoicesFor,
  shapeOf,
  sizeChoicesFor,
  specFor,
  type MainMix,
  type Tier,
} from '@/config/studioShape'
import {
  buildShotRefineBrief,
  copyPlanFor,
  fallbackPrompt,
  promptsForProduct,
  refOrder,
  type ProductInfo,
  type PromptCtx,
} from '@/lib/detailPrompts'
import { mergeProductRefs, productImportMessage } from '@/lib/productRefs'
import { useJobCenter, type SubmitArgs } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { AiFillButton } from '@/components/AiFillButton'
import { ProductAssetPickerButton } from '@/components/ProductAssetPicker'
import { buildDetailFillBrief } from '@/config/prompts'
import { FILL_FORM_FIELDS, type ProductAsset } from '@/api/types'
import { useToast } from '@/context/ToastContext'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'

/**
 * ===========================================================================
 * 主图 · 套图 · 详情 —— **三个页面合并成一个工作台（2026-09-29）**
 * ===========================================================================
 *
 * 合并前是三条侧栏入口、三个页面、三套表单：
 *   `/detail-page` 主图与详情 · `/ai-set` 商品套图 · `/localize` 平台图（成片派生）
 *
 * 用户的原话只有一句：「这 3 个页面合并为一个」。
 * 合并的判据不是「长得像」，而是**它们共用同一条操作路径**：
 *   输入物（产品信息 / 成片 + 平台）→ 视觉决策（模型 / 张数 / 比例 / 尺寸）
 *   → 核对并花钱提交。
 * 差别只在「产出什么形态」，那是**根决策**，不是三个页面。
 * 拆成三页的代价是实打实的：同一批产品信息要在两页各填一遍，任务历史分散在
 * 三个 `jobsFor` 键下（在套图页看不到详情页刚提交的任务）。
 *
 * 合并后的结构（沿用本项目生成页骨架，没有新骨架）：
 *   页签外平台条 ：投放平台 —— 换它等于换整套规格
 *   页签外规格条 ：A+ 档位与移动端资产 —— 只在「平台详情模块」形态下出现
 *   页签内       ：出图数量 → 模型与规格（2026-10-08 重切，见下面 PANES）
 *   右栏顶部     ：提交栏（价格与生成按钮同一行、吸顶，2026-10-08 全站统一到这里）
 *
 * ⚠️ 2026-09-30 页签外那行**一级 chip-row 已删**（用户原话「详情页跟主图合并为一页，
 * 然后在输入的时候选择就可以」）：原来把「电商主图 / 详情页模块」做成页签外的一级
 * 选择、形态藏在页签 1 里，等于同一件事（挑一种产出）被拆在两层。
 * 现在 3 个形态平铺在页签 1 的「产出形态」控件里，一行选完 ——
 * 分组层（`STUDIO_GROUPS` 一族）整块从 `config/studioModes.ts` 移除，
 * 留下的 `STUDIO_MODES` 是唯一清单、唯一排序。
 *
 * ⚠️ 同日「商品套图」（`set` / `ai-set`）**也下架了**，用户原话对着形态控件上的
 * 那一项：「这套图跟详情页重复了」—— 判据是**产出物**：它出的「主图 + 场景 + 卖点」
 * 就是这一档按平台官方模块顺序出的同一批东西，两个选项选哪个都得不到不同结果。
 * 跟着删掉的还有只有它用的输入与提示词（类目 / 场景倾向 / 画面文字 /
 * `buildSetPrompts`），页面因此少了一整支 `isSet` 三元。详见 `studioModes.ts`。
 *
 * 三条必须守住的点（都有冒烟断言兜着）：
 *   1. **形态清单只在 `config/studioModes.ts` 一份**，页面不许再写一遍名字或 app_slug；
 *   2. **右栏取数键与提交写入键同源**，全部形态统一 `detail-page`；
 *   3. **平台的「入口」全页只有一个**（页签外平台条），不另立第二个下拉。
 *
 * ⚠️ 2026-09-29「派生」（成片改版）**整块下架**，用户原话
 * 「派生这个弄太复杂了，不要了」。它原本是最后一个页签，为了它这一页多养了
 * 6 个字段（成片 / 目标语言 / 原图文字 / 分辨率 / 原图来源平台 / 产品名）、
 * 一个 `sourcePlatform` 字段名（就是为了避免与投放平台串扰）、
 * 一套 `isDerive` 三元分支（散在 20 多处）与一条「改图不重做」提示词主线。
 *
 * 这不是「少了个功能」，是**这一页只剩一种工作了**：从商品信息出图。
 * 判据变量（`isDerive`）随之消失 —— 所有 `a ? b : c` 里那个 `a` 都不再需要，
 * 留下来的每个控件都真的会改变出图结果。
 */

/**
 * 页签（不是步骤）。
 *
 * 2026-09-28 用户驳回了原来的三步向导，原话：「不需要准备素材」。
 * 「准备素材」（产品名 / 卖点 / 参考图）本来就该和模型、比例、张数放在一起看 ——
 * 拆成独立一步只是多按一次「下一步」；而「上一步/下一步」还顺手把价格与生成按钮
 * 藏到了流程末端，用户不走到最后一步看不到要花多少钱。
 * 所以页签的作用只有一个：**把左栏按决策域切开**，随时可跳，报价与提交按钮常驻。
 *
 * 2026-09-29 上午：合并三个页面后是两个页签，加「派生」成为三个。
 * 2026-09-29 下午：用户原话「出图参数部分放到最后，目标与参数放第一步，
 * 生成模型放最后」，紧接着又要求「精简」。于是：
 *   · 「出图参数」从页签 1 里拆出来独立成一步，页签 1 只剩「目标与参数」；
 *   · 原「核对与生成」页签**整个删掉**。它那张「参数核对」卡是把页签 1 与页签 3
 *     的字段逐行复述一遍 —— 平台与模式、产品与卖点、产品图、模型与规格、图内文字，
 *     全是用户三秒前自己填的。同一批数据在两屏各写一遍（铁律 7「减噪音」），
 *     而且排位还倒置：核对在第 3 步之前，核完还能回去改参数，改了就得再回来核一次。
 *     删掉不丢能力：「本批出图结构」并入出图参数页签，报价与提交按钮本来就在右栏顶部常驻，
 *     任何页签都在视线内；A+ 档位与移动端资产在页签外规格条上，不依赖这张卡。
 *   · 紧接着「派生」页签**也整个删掉**（用户原话「派生这个弄太复杂了，不要了」）。
 *
 * 这次拆分的判据是**同一个字段组不该跨两个决策域**：
 *   「这一批要出什么」（形态 / 产品 / 卖点 / 参考图）与
 *   「怎么出」（模块顺序 / 比例 / 张数 / 模型）是两件事 ——
 *   前者的答案来自买家与商品，后者的答案来自平台规格与预算。
 *   上一版把它们压在第 1 步里，第 1 步有 8 个控件，左栏一长就难用（铁律 7）。
 *
 * ⚠️ 改页签数必须同时升 `step_detail-page` 这个 key（见 useLocalStorage 那行）。
 * 这次页签从 3 个变 2 个、**index 又变了**（旧 0=目标、1=出图参数、2=派生），
 * 老的 2 会落在一个不存在的页签上，所以必须升到 `_v5`。
 * 代价只有「页签位置重置一次」—— 表单草稿键 `detail-page` 没动，内容不丢。
 *
 * 2026-10-09：**产品信息放回页签 1**，页签 2 个 → 3 个（用户原话「产品信息放回 step1，
 * 需要 3 step」）。上一轮（2026-10-08）它被整块搬到右栏，见 `stepSource` 上方那段沿革。
 *
 * 那一次搬的判据是「决策域不同」，这条判据**今天仍然成立**；错的是**层级**：
 * 它是「拿什么去出」，是填表的**第一步**，不该和结果区抢右栏 ——
 * 右栏的活是「我这次出了什么、上次用的哪组素材」，不是「先填这个」。
 * 所以搬回来的是**位置**，不是推翻判据：它仍然不进报价（改多少次都不花钱）。
 *
 * 三步 = 填什么 → 出几张 → 怎么出，与「一次只回答一个问题」对齐。
 * ⇒ index 0 的含义又变了，key 必须再升一版（`_v6` → `_v7`）。
 */
const PANES: WorkflowTab[] = [
  { id: 'product', title: '产品信息', desc: '产品名、卖点与产品图 —— 决定画面里是什么' },
  { id: 'count', title: '出图数量', desc: '这一批出几张、出哪几屏 —— 与报价直接相关' },
  { id: 'spec', title: '模型与规格', desc: '画布尺寸与出图模型 —— 决定每一张多大、多贵' },
]

/**
 * 套图模式的类目清单 —— 随「商品套图」于 2026-09-30 整块删除。
 *
 * 它原来上提到 `config/studioShape.ts` 的 `SET_CATEGORIES`，理由是批量工作台要用
 * 同一份。套图下架后「类目」这个输入本身没了（提示词那条路径已删），
 * 清单跟着走 —— 留一个没人读的选项表就是死枚举。
 * 提示词层没有「类目」这一项之后，`ProductInfo` 只剩产品名与卖点两项。
 */

/* -------------------------------------------------------------------------
 * 形态层（张数选项 / 档位 / 模式默认值 / 尺寸写法）已上提到 `config/studioShape.ts`。
 * 上提的理由只有一个：**批量工作台**（`pages/BatchStudio.tsx`）要用同一份。
 * 留在这里的话，两个页面必然各写一份「Amazon 基本 A+ 是 970 系」这类判断，
 * 改一处漏一处不会报错，只会让两个页面出不同规格的图。
 * ------------------------------------------------------------------------- */

/* =========================================================================
 * 产品信息 → 提示词：在 `lib/detailPrompts.ts`（两个页面共用同一份）。
 * ========================================================================= */

type FormState = {
  mode: StudioModeId
  /** 投放平台 —— 全页只有这一个平台字段。决定尺寸、图内文字语言、卖点框架与禁词 */
  platform: string
  /** 规格档位（目前只有 Amazon 用，且只在「平台详情模块」模式下渲染） */
  tier: Tier
  /**
   * 是否同时产出**移动端专属资产**。
   * 官方对高级 A+ 全宽模块要求桌面 + 移动两份独立文件（1464×600 / 600×450），
   * 这两份比例不同、不能互相裁切，所以要单独出图 → 张数与花费都会增加，
   * 默认关闭，由用户显式打开。
   */
  withMobile: boolean
  productName: string
  sellingPoints: string
  /**
   * 详情页逐屏**为什么这么排**，由平台规格的模块顺序 + 每屏的 `beat` 角色派生
   * （见 `config/detailPlan.ts`），**不受用户编辑**。
   *
   * 上一版这里是 `modules: string` —— 一个可编辑的「详情页模块顺序」多行文本框，
   * 用户写几行词就是几屏。2026-09-29 用户报障「详情页的生成提示词太傻了……
   * 而且详情页模块顺序不要写出来」后被删掉：
   *   · 用户手写的词命不中官方模块名（写「解决方案」而不是「详情 · 卖点拆解」），
   *     匹配不上就退化成同一句通用模板 → N 份提示词除模块名以外逐字相同；
   *   · 规划本来就不该由用户做 —— 它由买家需求排序与平台规格决定。
   * 现在用户在界面上**看到**的是规划结果（「本批出图结构」那张卡），不是输入框。
   */
  ratio: string
  /**
   * 张数 —— **只有「模仿版式」与「平台详情模块」两档读它**。
   *
   * ⚠️ 「电商主图」档的张数是 `mainMix` 之和，这个字段在那一档**不参与**
   * （2026-10-07 加配比时定的）。两个入口写同一个值迟早对不上：
   * 用户把「场景」加到 3，这个字段还写着 4，提交出去的是几张就没人说得清。
   * 所以主图档的渲染分支里**没有张数输入框**（见 `输出张数` 那个 Field）。
   */
  outputs: number
  /**
   * **主图配比**（只有「电商主图」档读）—— 白底 / 场景 / 卖点 / 细节各几张。
   *
   * 2026-10-07 用户口径：参考竞品那块「自定义配置」（白底图 1 / 场景图 3 /
   * 卖点图 3 / 其他 0，最少 7 张），选了「补上，可自由配比」。
   *
   * 放在 `form` 里而不是页面 state：它是**这一批的规格**，与张数同级 ——
   * 刷新后要还在，切形态/换平台也要能按同一套规矩收敛（见 `switchMode`）。
   * 同一份数据在两个地方各存一遍是本项目反复复发的那个错。
   */
  mainMix: MainMix
  /**
   * **勾选的官方模块名**（只有「平台详情模块」档读）。
   *
   * 模块名 → **份数**（0 = 这个模块不出；缺省 = 1 份）。
   *
   * 2026-10-08 用户口径：「详情跟主图一样可以选择」—— 主图那一档是「各类张数」
   * （每类 − / +），这里原来只有勾选框。换成份数之后两边的操作语言一致，
   * 而「份数之和 × 每份张数 = 张数」也顺手替掉了那个单独的数字输入框
   * （同 §2-3：配比就是张数，不是第二维）。
   *
   * ⚠️ 存**完整表**不存稀疏覆盖：份数 0（这个模块不出）与「还没调过」
   * （按默认 1 份）必须能区分开，而稀疏表的「缺省」只有一个含义。
   * 归一化走 `moduleCountsOf` —— 遍历的是当前平台的 `spec.modules`，
   * 所以换平台之后失效的键名自动被丢掉，不需要单独的迁移步骤。
   *
   * ⚠️ 名字**刻意不叫 `modules`** —— 2026-09-29 那个被删掉的
   * 「详情页模块顺序」文本框用的就是 `form.modules`（一个可编辑的字符串），
   * 冒烟里有一条 `!page2.includes('form.modules')` 守着它别长回来。
   * 那个字段把**规划**推给用户，这一个只是**在官方模块里配份数** ——
   * 两件事，名字不同才不会让人（和那条断言）把后一个当成前一个复活。
   */
  moduleCounts: Record<string, number>
  /**
   * **整批统一尺寸**（只有「平台详情模块」档渲染入口）。
   *
   * 2026-09-30 用户口径：「这个尺寸有默认，但是可以选择，数量也是」。
   * 空字符串 = **官方尺寸**（逐张按平台模块规格，默认值，也是这一档开出来时的原状）；
   * 非空 = 本批每一张都用它（选项来自平台档案自己的比例，见 `sizeChoicesFor`）。
   *
   * 它只覆盖**桌面图位** —— 移动端资产的尺寸是官方硬要求，见 `shapeOf`。
   */
  batchSize: string
  /**
   * 分辨率。**不给 UI 入口** —— 出图一直按 2k 走（与原「主图与详情」「商品套图」
   * 两页一致：970×600 这类官方尺寸本来就是按比例放大，4K 只是更贵）。
   *
   * 字段留着而不是删掉，因为它同时是**报价乘数**与提交参数
   * （后端 `pricing.py` 的 `_normalize_resolution`、`contracts.JobCreate`）。
   * 删了字段会让报价落回「未知档」的默认乘数 —— 那是**静默改价**，
   * 比留一个只有默认值的槽位危险得多。
   * 它原来唯一的入口在派生页签（改的是成片，用户对成品像素有明确预期），
   * 派生下架后只剩这个默认值。
   */
  resolution: '1k' | '2k' | '4k'
  model: string
  refs: string[]
  layoutRefs: string[]
}

/**
 * 产品图张数上限 —— **一处真源**：`RefUploader` 的 `max` 与「从商品库带入」的
 * 截断判断都读它。写两个 6 就会出现「带入时按 8 张算、控件只收 6 张」这类
 * 只差两位数的错，而界面上看不出任何异常（图少了一张，没人会去数）。
 */
const PRODUCT_REFS_MAX = 6

const DEFAULT_PROFILE = getPlatform(DEFAULT_PLATFORM_ID)

const DEFAULTS: FormState = {
  mode: DEFAULT_MODE,
  platform: DEFAULT_PROFILE.id,
  // 默认**基本档**：所有 Brand Registry 卖家都有，不会出现「按 1464 出图，
  // 结果账号只有基本 A+」的白做。有高级资格的自己去规格条切。
  tier: 'basic',
  withMobile: false,
  productName: '',
  sellingPoints: '',
  // 张数与比例从平台档案派生，不手写 —— 上一版这里写死 5，主图模式明明写着
  // 「一次 4 张」，两条数字各写一遍就必然对不上。
  ratio: defaultsFor('main', DEFAULT_PROFILE).ratio,
  outputs: defaultsFor('main', DEFAULT_PROFILE).outputs,
  // 主图配比的默认 = 四类各一张（就是加配比之前那套固定四张的构成）
  mainMix: defaultMainMix(),
  // 空数组 = 全部模块都出（与加勾选之前的行为一致）
  // 空表 = 归一化后每个模块各 1 份（与加「份数」之前的行为一致）
  moduleCounts: {},
  // 空 = 官方尺寸逐张匹配（这一档的默认，不是「没选」）
  batchSize: '',
  resolution: '2k',
  model: DEFAULT_FAMILY_ID.image,
  refs: [],
  layoutRefs: [],
}

export function DetailPageStudio() {
  const [sp, setSp] = useSearchParams()
  const { value: form, patch } = useDraft('detail-page', DEFAULTS)
  // useLocalStorage 自带 hw_ 前缀，不要重复写；页签位置也持久化，切页回来不丢。
  // ⚠️ 页签从 3 个变 2 个、末位下标消失 → key 必须升（见 `PANES` 上方的说明）。
  // 草稿键 `detail-page` 没动，表单内容不丢。
  const [step, setStep] = useLocalStorage<number>('step_detail-page_v7', 0)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()

  const flow = useGenerateFlow()

  /* ------------------------------------------------------------------ *
   * 从商品库带入产品信息（2026-10-08）
   *
   * 用户口径：「这个可以让商品详情页可以直接引用，就不需要自己输出产品图片
   * 跟文案那些」。所以一次带入三样：**产品名 + 卖点 + 产品图**。
   *
   * ⚠️ 带入的是 `form` 里已有的那三个字段，**不新增第二份 state**。
   * 另存一份「引用的商品」会让「用户改过产品名之后以哪个为准」变成一道没人
   * 答得上来的题 —— 而提示词只认 `form`，那份影子状态会被静默忽略。
   * 带入之后它就是普通的手填值，用户可以随便改。
   *
   * 图片是**追加去重**（商品图在前，主图仍是商品的第一张），不是替换：
   * 替换会把用户自己已经传好的图悄悄删掉，而那个损失在界面上一闪就过去了。
   * 上限仍受 RefUploader 的 `max` 约束，被截断时如实说。
   * ------------------------------------------------------------------ */
  const toast = useToast()

  const applyProductAsset = useCallback(
    (asset: ProductAsset) => {
      const incoming = asset.images.map((i) => i.url).filter(Boolean)
      /*
       * 合并规则（商品图在前、追加不替换、如实报截断）是**六个页面共用的一份**，
       * 见 `lib/productRefs.ts`。这里曾经的写法与它是逐字一致的 ——
       * 提取出去是为了让「社媒图页带进来的图顺序和这里不一样」这类漂移没有生存空间。
       */
      const { urls: merged, dropped } = mergeProductRefs(form.refs, incoming, PRODUCT_REFS_MAX)
      patch({
        productName: asset.title,
        sellingPoints: asset.sellingPoints,
        refs: merged,
      })
      toast.success(
        productImportMessage(asset.title, { refs: merged.length, dropped, max: PRODUCT_REFS_MAX }),
      )
    },
    [form.refs, patch, toast],
  )

  /*
   * 这一页**不再有提示词预览入口**（2026-10-07 用户口径：「提示词隐藏也可以的」）。
   *
   * 原先是「本批出图结构」卡右上角一个按钮 + 一个弹层（2026-09-30 加的，
   * 当时的原话是「生图前可以浏览一下多个提示词，再去生成」）。删掉它有两条理由：
   *
   *   1. 它**与既有铁律冲突** —— 提示词只在 `PriceConfirmDialog` 展示一份
   *      （见 `components/studio.tsx` 里那段「唯一展示处」）。那里已经有
   *      「查看将发送给模型的完整提示词」和复制按钮，这一页再挂一份
   *      就是同一件事的两份实现，还多养了一个弹层与一份只在本页的 state。
   *   2. 用户这一轮的要求是「简单点」—— 页面上少一层控件。
   *
   * ⚠️ 删的只是**展示**，而且展示本身没丢：`doGenerate` 照旧逐张拼 `prompts`
   * 提交，同时把这些词以 `shotPrompts` 交给提交前的确认弹窗 ——
   * 那一步离花钱更近，也更该看。不接过去的话这里就只剩顶层那份**兜底通用词**
   * （`fallbackPrompt`），而实发的是 N 份逐张词：预览与实发不是同一份。
   */

  /**
   * **当前生效的形态** —— 就是「目标与参数」里选的那个。
   *
   * 上一版这里是个三元：派生页签固定取「多语言版」、出图页签取用户选的形态，
   * 好让后面所有 `activeMode.appSlug`（报价、提交）与 `modeName(mode)`（结果标题、
   * 提示词）都不分叉。派生整块下架后，这个三元只剩一个分支 ——
   * 于是它连「分支」都不算了，直接就是用户选的形态。
   *
   * ⚠️ 读的是 `form.mode` 而**不是** `sp.get('mode')`：地址栏那个参数是
   * **一次性入站指令**（见下面那个 effect），消费完就摘掉了，
   * 它不代表当前形态。
   */
  const activeMode = getMode(form.mode)
  const mode = activeMode.id

  /**
   * 地址栏 `?mode=` —— **一次性入站指令**：
   * 铺该形态的默认值 + 回第 1 步（`/ai-set` 老书签走这条）。
   *
   * 为什么要处理「回第 1 步」这件看起来多余的事：`step` 是**持久化**的
   * （`step_detail-page_v7`），跨地址生效。用户上一轮停在末尾页签，
   * 再去点 `/ai-set` 老书签，如果不重置页签，就会落在一个「看不出形态」的页面上 ——
   * 页签 1 是产品信息（产品名 / 卖点 / 产品图，2026-10-09 起），
   * 用户在末页签上只看到规格，既不知道自己刚被切成了哪个形态、也没法马上补素材
   * （这是真跑出来的，不是推测；那一级 chip-row 还在的时候更明显：
   * 顶部 chip 与页内内容互相矛盾）。
   *
   * 消费完就 `delete('mode')`，而不是留着。留下的问题：
   *   · 刷新时又翻译一遍，而那时用户可能早已手动切了形态 → 把用户的选择按回去；
   *   · 用户切了形态之后地址栏还挂着旧 `mode=`，地址与界面自相矛盾。
   * 与 `?vtpl=` 同构（视频工作台那处也是「铺完立刻摘掉」）。
   * 「可分享」这件事由 `/ai-set` 这类**别名路由**承担，不需要 mode 常驻。
   */
  useEffect(() => {
    const raw = sp.get('mode')
    if (!raw) return
    const target = getMode(raw)
    // 手改地址栏塞了个不存在的 id（含已下架的 `derive-language`）：
    // `getMode` 已兜底成默认形态（兜底后 id 与原值不等），这里不动页签，也不报错
    if (target.id !== raw) return

    const d = defaultsFor(target.id, profile, { detail, withMobile: form.withMobile })
    patch({
      mode: target.id,
      outputs: d.outputs,
      ratio: target.id === 'aplus' ? form.ratio : d.ratio,
      // 与页内切形态同一条规矩（见 `switchMode`）：进主图档就铺默认配比
      mainMix: target.id === 'main' ? defaultMainMix() : form.mainMix,
      moduleCounts: moduleCountsOf(detail, form.moduleCounts),
    })
    setStep(0)
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('mode')
        return next
      },
      { replace: true },
    )
    // deps 只放 sp：这个 effect 的触发条件是「地址栏变了一次」，
    // 其余读到的 form/profile/detail 都只是那一刻的快照，放进 deps 会让
    // 用户每次在页面上改个参数都重跑一遍、把形态按回地址栏里的旧值。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp])

  // 平台档案 —— 页面里所有「这个平台该什么样」都从它取，不在页面里再写一遍。
  const profile = getPlatform(form.platform)
  // 档位再往下一层：同一平台两档的官方尺寸不同（970×600 ↔ 1464×600），
  // 两档比例不通用，所以「出多大」最终由 specFor 决定，不由页面猜
  const detail = specFor(profile, form.tier)

  /**
   * 模块份数的**完整表**（`form.moduleCounts` 归一化到当前平台规格）。
   *
   * 界面（`ModulePicker`）、报价（`batchCount` → `countOf` 读 `shots.length`）
   * 与提交（`args.prompts` / `args.ratios`）**都读这一份** ——
   * 三处各算一次的话，改一个模块的份数就会出现「界面 8 张、报价按 8 张、
   * 发出去的却是 6 份词」这种没人能一眼看出的错位。
   */
  const moduleCounts = useMemo(
    () => moduleCountsOf(detail, form.moduleCounts),
    [detail, form.moduleCounts],
  )

  /** 份数 > 0 的模块名（官方顺序）—— `shapeOf` 与提示词层要的是这个名字清单 */
  const pickedModules = useMemo(
    () => detail.modules.filter((m) => (moduleCounts[m.name] ?? 0) > 0).map((m) => m.name),
    [detail, moduleCounts],
  )

  // 模型清单 —— 只用它取「当前模型能出哪些比例」（候选表的第二三段从这儿补）
  const { list: listModels } = useModels()

  /**
   * 草稿里可能躺着非法张数（旧版本存过别的上限，或手改过 localStorage）——
   * 夹一次并**回写**。不回写的话 `planDetailShots` 会照着它规划：
   * 报价按 999 张算、逐张清单也真有 999 行。
   * 收敛放在这里而不是只放输入框里，是因为 `form.outputs` 还有别的入口
   * （草稿恢复、`?mode=` 换形态时 `defaultsFor` 的兜底值）。
   */
  useEffect(() => {
    const fixed = clampCount(form.outputs)
    if (fixed !== form.outputs) patch({ outputs: fixed })
  }, [form.outputs, patch])

  /**
   * 主图配比同样夹一次并回写 —— 同一条判据，只是这回的对象是数组。
   *
   * 草稿里可能是**上一版 `MAIN_SHOTS` 的配比**（长度不对），或手改过的
   * `[-3, 0, "2", …]`。不收敛的话 `mixTotal` 会算出负数或 NaN：
   * 报价那一栏直接变成 `NaN`，而 `mainShotsFor` 按坏数组展开，
   * 逐张清单的行数与「当前共 N 张」对不上。
   */
  useEffect(() => {
    const fixed = normalizeMix(form.mainMix)
    if (fixed.join(',') !== (form.mainMix ?? []).join(',')) patch({ mainMix: fixed })
  }, [form.mainMix, patch])

  /**
   * 形态显示名。
   * 「平台详情模块」这一档按平台叫法显示（Amazon 就是「Amazon A+」），
   * 名字来自平台档案的 `tabName`，页面里不写第二份。
   */
  const modeName = (id: StudioModeId) => (id === 'aplus' ? detail.tabName : getMode(id).name)
  const activeName = modeName(mode)
  const activeDesc = activeMode.desc

  /* ------------------------------ 逐屏解构 ------------------------------ */

  /*
   * 逐张解构整块上提到 `config/studioShape.ts` 的 `shapeOf()`。
   *
   * 上提的理由是**它必须与批量工作台算出同一份**。留在页面里的话，
   * 批量页要么 import 这个页面（把整页拉进来），要么自己再写一遍
   * 「主图四张怎么循环、详情页几屏怎么规划」—— 后者的后果不是报错，
   * 而是「详情页说这批 4 张、批量页说 6 张」，两边都能提交，报价各按各的算。
   *
   * 顺带消掉的还有下面三处（`batchCount` / `ratioChoices` / `countChoices`），
   * 它们是同一族派生，分开留在两页一样会分叉。
   */
  const { shots, plannedShots } = useMemo(
    () =>
      shapeOf({
        mode,
        outputs: form.outputs,
        ratio: form.ratio,
        detail,
        withMobile: form.withMobile,
        batchSize: form.batchSize,
        // 主图档读配比、详情档读模块份数 —— 两个都传，各自只取自己那一份
        mix: form.mainMix,
        modules: pickedModules,
        counts: moduleCounts,
      }),
    [
      mode,
      form.outputs,
      form.ratio,
      form.mainMix,
      pickedModules,
      moduleCounts,
      detail,
      form.withMobile,
      form.batchSize,
    ],
  )

  /**
   * 本批实际会出的尺寸（去重）—— 用于核对卡、尺寸清单与右栏出图比例。
   * 它取的是**覆盖之后**的尺寸：用户把整批改成 1:1 时，这里就该只剩 `1:1`。
   */
  const sizesInBatch = useMemo(() => [...new Set(shots.map((s) => s.size))], [shots])

  /* ------------------------------ 数量与选项 ------------------------------ */

  const batchCount = countOf(mode, form.outputs, shots)

  /**
   * 当前模型**真正能出的比例** —— 候选表的「平台常用」之外的都从它补。
   *
   * 唯一真源是后端下发的 `capabilities.aspect_ratios`
   * （实测：duomi 直连 13 个，songshuai 网关 6 个）。前端**不维护第二份** ——
   * 本项目 09-29 那个「选了 4:5 到手方图还照样扣费」就是两份清单分叉的后果。
   * 后端没说（旧网关模型是空对象）或清单还没拉到时是 `undefined`，
   * 此时候选只剩平台常用那几档，**不凭空虚造**一份通版比例表。
   */
  const modelRatios = useMemo(
    () => listModels('image').find((f) => f.id === form.model)?.capabilities?.aspect_ratios,
    [listModels, form.model],
  )

  const ratioChoices = useMemo(
    () => ratioChoicesFor(mode, profile, modelRatios),
    [mode, profile, modelRatios],
  )

  /**
   * 「本次要哪几个模块、合计几张」—— **都是派生量，不另存 state**。
   *
   * ⚠️ 2026-10-07 替换掉了上一轮的三档前缀预设（`coveragePresets`）。
   * 用户看过竞品那套完整勾选列表后的口径是「加 16 个模块逐个勾选」——
   * 但我们的模块**不是固定 16 个**，它跟着平台规格走，所以这份清单渲染的是
   * 当前平台的 `spec.modules`（Amazon 基本档 5 / 淘宝 8 / TikTok Shop 8…）。
   *
   * ⚠️ 2026-10-08：`slotLimit`（「超过就从头循环」那句提示的底数）删掉了。
   * 有了份数之后**不存在「超过」这回事** —— 总张数就是各模块份数之和，
   * 想多要一轮就把那个模块 +1，不再是从官方序列头开始循环。
   * 留着它只会在提示里说一句已经不可能发生的话。
   */
  const moduleCount = pickedModules.length
  /** 勾选模块合计多少张 —— 与 `ModulePicker` 头部那句、与报价**同源** */
  const moduleShotTotal = moduleShotsTotal(detail, form.withMobile, moduleCounts)

  /**
   * 输出尺寸的候选项（**整批统一一个尺寸**，2026-09-30 用户口径）。
   * 只有「平台详情模块」这一档渲染它 —— 别的档位有比例选择器，
   * 但那两处用的是同一份分组结构与同一个控件（少一份实现就少一处分叉）。
   */
  const sizeChoices = useMemo(
    () => sizeChoicesFor(detail, profile, modelRatios),
    [detail, profile, modelRatios],
  )

  /**
   * 换平台 / 换档位之后，把「整批尺寸」收敛回新候选表 ——
   * 不在表里就回官方尺寸。不收的话，下拉会乖乖显示第一项、
   * 而 state 里还是旧值：**界面看着改过来了，提交出去是另一个尺寸**。
   */
  const fitBatchSize = (raw: string, nextProfile: typeof profile, nextDetail: DetailSpec) =>
    flatChoices(sizeChoicesFor(nextDetail, nextProfile, modelRatios)).some((o) => o.value === raw)
      ? raw
      : ''

  /**
   * 出图分辨率固定 2k —— 与原「主图与详情」页一致，
   * 不新开入口（970×600 这种官方尺寸本来就是按比例放大，4K 只是更贵）。
   * 上一版唯一的分辨率入口在派生页签，已随派生下架 —— 现在它就是字段的默认值。
   */
  const batchResolution = form.resolution

  /* ------------------------------ 提示词组装 ------------------------------ */

  /**
   * 提示词上下文 —— 与产品无关的那部分，全批次只有一份（见文件上方说明）。
   */
  const promptCtx = useMemo<PromptCtx>(
    () => ({
      profile,
      detail,
      mode,
      modeName: activeName,
      ratio: form.ratio,
      resolution: batchResolution,
      outputs: Math.max(1, form.outputs),
      batchSize: form.batchSize,
      shots,
      plannedShots,
      layoutRefCount: form.layoutRefs.length,
    }),
    [
      profile,
      detail,
      mode,
      activeName,
      form.ratio,
      batchResolution,
      form.outputs,
      form.batchSize,
      shots,
      plannedShots,
      form.layoutRefs.length,
    ],
  )

  /**
   * 「当前这一份」产品信息 —— 就是页签① 表单里的两项。
   *
   * 批量那一侧（每一行各带自己的两项、逐行调 `promptsForProduct`）整块搬去了
   * `/batch`。搬家的理由不是「UI 放不下」，而是**批次的寿命不一样**：
   * 这里的状态是 `useState`，刷新即失；批次要能关掉页面、明天回来接着补图。
   */
  const soleProduct = useMemo<ProductInfo>(
    () => ({
      productName: form.productName,
      sellingPoints: form.sellingPoints,
    }),
    [form.productName, form.sellingPoints],
  )

  const soleRefCount = form.refs.length

  /*
   * 逐屏提示词 / 总提示词 —— 同一个入口的派生物，只认 `ProductInfo`。
   *
   * 上一版这里还有 `header` 与 `setInput` 两个 `useMemo`，是拆层时留下的中间变量：
   * 声明了但**从没被读过**（它们的返回值在 `promptsForProduct` / `fallbackPrompt`
   * 内部各自构造一次）。`noUnusedLocals` 是关的，所以它们安静地挂了很久 —— 一并删掉。
   */
  const prompts = useMemo(
    () => promptsForProduct(promptCtx, soleProduct, soleRefCount),
    [promptCtx, soleProduct, soleRefCount],
  )
  const prompt = useMemo(
    () => fallbackPrompt(promptCtx, soleProduct, soleRefCount),
    [promptCtx, soleProduct, soleRefCount],
  )

  /**
   * 模仿版式的逐张分配 —— 与提示词层**共用 `copyPlanFor`**。
   *
   * 不共用的话，卡片会说「本张沿用参考版式第 3 张」而提示词让模型用第 1 张，
   * 两边都不报错，只有出图结果对不上。
   *
   * ⚠️ 2026-10-07 从「出图参数」那一段**上移到这里**：确认弹窗前那份
   * 逐张提示词清单（`shotPrompts`）也要用它认版式序号，而它长在 `doGenerate`
   * 下方 —— 函数体内引用后声明的 const 运行期虽然没事，但那属于「靠调用时机
   * 恰好成立」，换个调用点就会 `Cannot access before initialization`。
   */
  const copySlots = useMemo(
    () => (mode === 'copy' ? copyPlanFor(batchCount, form.layoutRefs.length) : []),
    [mode, batchCount, form.layoutRefs.length],
  )

  /**
   * 「AI 填充」不让点的原因（有值 = 禁用，不隐藏）。
   *
   * 前置条件只有**产品名**一条 —— LLM 是纯文本的（看不了参考图），
   * 没有产品名它只能编，而编出来的卖点看着像真的，用户会一路用到出图才发现
   * 产品是它造的。填哪几个字段由 `FILL_FORM_FIELDS.detail` 说了算
   * （这一档只填卖点 —— 分节结构与张数是平台规则决定的，不归 AI）。
   *
   * 2026-10-08 **从下方上移到这一段**：逐张提示词的「AI 重写」用的是同一条
   * 前置条件（没产品名它照样会编），而 `shotRefine` 是 `const` ——
   * 引用后声明的 `const` 在回调里虽然「运行期恰好没事」，但那属于靠调用时机成立，
   * 换个调用点就 `Cannot access before initialization`（`copySlots` 当初就是因此上移的）。
   * 同一个前置条件写两遍就是两处会漂，所以只留这一份。
   */
  const detailFillBlocked = !form.productName.trim()
    ? '先填产品名称 —— AI 需要知道这是什么产品才写得对'
    : undefined

  /**
   * 一张产出的**定位标签** —— 三处共用，抽成一份实现。
   *
   * 三处：① 「本批出图结构」卡逐行；② 确认弹窗里那张的标题；
   * ③ AI 重写的 brief（`buildShotRefineBrief` 的 `label`）。
   * 三处各写一遍的话，改了一处就会「卡片说第 2 张、AI 收到的是第 3 张」——
   * 而 AI 会照着错的定位重写，产物通顺、看不出来。
   *
   * 主图 / A+ 取 `shots[i]` 的名字与尺寸；模仿版式取 `copySlots[i]` 的版式序号
   * （同一个 `copyPlanFor`，不重算一份 —— 见 `copySlots` 的说明）。
   */
  const shotLabel = useCallback(
    (i: number): string =>
      shots[i]
        ? `${shots[i].name} · ${sizeLabel(shots[i].size)}`
        : copySlots[i]
          ? `参考版式第 ${copySlots[i].layoutNo} 张 · ${sizeLabel(form.ratio)}`
          : `第 ${i + 1} 张`,
    [shots, copySlots, form.ratio],
  )

  /**
   * 逐张提示词的「AI 重写」上下文（`PriceConfirmDialog` 的 `shotRefine`）。
   *
   * 用户口径：「可以修改提示词，还有提示词生成可以多一个 AI 按钮重置」。
   * `briefs` 与 `shotPrompts` **下标一一对应**：弹窗按同一下标取，取错位就是
   * 「拿 A 张的上下文重写 B 张的词」，而产物一样通顺（`shotLabel` 同源同理）。
   *
   * `blocked` 直接用 `detailFillBlocked`（没产品名不该让 AI 去编），
   * 不另写一句 —— 同一个条件两处措辞，改一处必漏一处。
   *
   * ⚠️ 如实说：**这一条当前不可达** —— 本页 `canSubmit = formOk && !needLayoutRef`
   * （`formOk` 就是「产品名非空」），没产品名时提交栏本来就是禁用的，
   * 弹窗打不开，也就没有那颗按钮可点。留着它是**第二道防线**：
   * 提交前置条件哪天松一格（比如允许先选平台后补产品名），
   * 这里不会跟着一起松 —— 而 AI 编出来的产品信息看着完全像真的。
   */
  const shotRefine = useMemo(
    () => ({
      briefs: prompts.map((_, i) =>
        buildShotRefineBrief(promptCtx, soleProduct, soleRefCount, shotLabel(i), prompts.length),
      ),
      sourcePage: 'detail-page',
      blocked: detailFillBlocked,
    }),
    [prompts, promptCtx, soleProduct, soleRefCount, shotLabel, detailFillBlocked],
  )

  const price = usePricePreview({
    appSlug: activeMode.appSlug,
    model: form.model,
    outputs: batchCount,
    resolution: batchResolution,
    aspectRatio: shots[0]?.size ?? form.ratio,
  })

  // 右栏取数键与提交写入键同源（`detail-page` + 当前形态的 `app_slug`）——
  // 合并前三个页面各读各的键，在这儿提交的任务在另一个页面的右栏里根本不出现。
  //
  // ⚠️ 第二参数是**档位收窄**：3 个形态的 `sourcePage` 相同、`app_slug` 不同
  // （detail-main / detail-aplus / detail-copy；下架的 `detail-long` 与 `ai-set`
  // 也仍在这个口径里 —— 库里还有它们的历史产物，只是形态不可选了）。
  // 只按页面键过滤的话，切到「平台详情模块」时右栏会把「电商主图」的产出
  // 一起铺在标题下面 —— 2026-09-29 用户报障的同源问题
  // （先在商品图页出现：「这商品图是 AI 模特图，场景图不需要」）。
  const jobs = jobsFor('detail-page', activeMode.appSlug)
  const outputs = outputsFor('detail-page', activeMode.appSlug)

  /**
   * 硬门槛。两类模式的要求不同：
   *   出图：必须有产品名（没有它后面所有参数都无从谈起）；
   *   模仿版式：还必须有参考版式图（没有它「模仿」无从谈起）。
   *
   * （派生那一档「必须有成片 + 必须有目标」已随派生一起删除。）
   * （批量那一档「至少一行可用」随批量搬去了 `/batch` —— 那一页的门槛是
   *   「表格里至少有一行合格」，与这里的 `form.productName` 无关。）
   */
  const formOk = form.productName.trim().length > 0
  // 模仿版式的参考版式图**两个形态共用同一条规则**，所以抽成一个判据 ——
  // 写两遍的话，某天改了「哪些模式需要版式图」，必然只改一处。
  const needLayoutRef = mode === 'copy' && !form.layoutRefs.length
  const canSubmit = formOk && !needLayoutRef

  /**
   * 禁用原因。**每一种「不可提交」都要有对应的那句话** ——
   * 使按钮变灰而不说原因，用户只能靠猜。
   */
  const missingReason = !formOk ? '请先填写产品名称' : needLayoutRef ? '请先上传参考版式图' : undefined

  /* ------------------------------ 切换动作 ------------------------------ */

  /**
   * 换形态 —— 等于换整条工作流：素材槽位、模块清单、张数与比例全都变了。
   * 切完回到第 1 个页签。
   *
   * 它是页面上**唯一**的形态入口（2026-09-30 之前还有一个页签外的一级 chip-row
   * 在它上面，现已整块删除 —— 见文件头「一级 chip-row 已删」那段）。
   * 所以这里不做任何「当前形态还是不是...」的判断：点哪个就是哪个。
   *
   * ⚠️ **不再往地址栏写 `?mode=`**。地址栏那个参数现在只当**入站指令**
   * （`/ai-set` 老书签），消费完就被摘掉（见上面那个 effect）。
   * 页内切形态再写回去，会让 effect 立刻把用户刚点的选择"翻译"一遍 ——
   * 多跑一轮 patch 不说，两处都在写同一个 `form.mode` 就是铁律 3 说的
   * 「同一字段两个入口」，迟早对不上。「可分享」由别名路由承担。
   */
  const switchMode = (id: StudioModeId) => {
    const d = defaultsFor(id, profile, { detail, withMobile: form.withMobile })
    patch({
      mode: id,
      outputs: d.outputs,
      ratio: id === 'aplus' ? form.ratio : d.ratio,
      // 形态换了，尺寸候选表也换了（只有 aplus 渲染这个控件）——
      // 不收敛的话，从 A+ 档带回主图档的那个值会静默留在 state 里
      batchSize: fitBatchSize(form.batchSize, profile, detail),
      /*
       * 主图配比回到默认（四类各一张）—— 与 `outputs` / `ratio` 同一条规矩：
       * 换形态 = 换整套默认值。带着上一轮的配比进主图档，会出现「结构卡 7 行、
       * 可输入框里写着 4」这种自相矛盾的状态（输入框那一档根本读不到配比）。
       */
      mainMix: id === 'main' ? defaultMainMix() : form.mainMix,
      /*
       * 模块份数**不动**：模块名是当前平台的，换形态不换平台时它仍然有效。
       * 上一版这里要 `normalizeModules` 一次，是因为「空数组 = 全选」那种表示
       * 需要按新平台的模块名重新对齐；换成完整表之后由 `moduleCountsOf`
       * 每次读取时归一（遍历 `spec.modules`），换形态这一步无事可做。
       */
    })
    setStep(0)
  }

  /**
   * 换平台 —— 比换形态影响更大：比例、模块序列、图内文字语言、禁词全跟着变。
   *
   * ⚠️ 2026-09-29：这里**不再需要**判断「模块清单有没有被用户改过」——
   * 详情页的逐屏序列不再由用户编辑（见 `FormState` 里那段说明），
   * 它由平台规格的模块顺序 + 每屏角色自动派生，换平台自然就换了一整套。
   * 旧版那段 `untouched` 比对随「详情页模块顺序」文本框一起删掉了。
   *
   * （上一版这里还要分流一次「派生页签的下拉是原图来源平台」，
   * 派生下架后这个下拉只有一种语义：投放平台。）
   */
  const switchPlatform = (id: string) => {
    const next = getPlatform(id)
    // 新平台不一定有升级档（只有 Amazon 有）。没有就回落基本档 ——
    // 否则会把「高级档」这个状态带到一个根本不存在的档位上。
    const nextTier: Tier = next.detailPremium ? form.tier : 'basic'
    const nextDetail = specFor(next, nextTier)
    const d = defaultsFor(mode, next, { detail: nextDetail, withMobile: form.withMobile })
    patch({
      platform: next.id,
      tier: nextTier,
      outputs: d.outputs,
      ratio: mode === 'aplus' ? form.ratio : d.ratio,
      // 尺寸候选表是按平台派生的（官方尺寸 + 该平台自己的比例），换平台必须复查
      batchSize: fitBatchSize(form.batchSize, next, nextDetail),
      /*
       * 勾选的模块名是**按平台**取的（「Image Header with Text」这种名字只属于
       * 某一个平台）—— 换平台必须过一遍交集，否则 `planDetailShots` 过滤后为空、
       * 一张图都不出，而界面上「已选 3/8」还亮着。
       * 交集为空时 `moduleCountsOf` 给每个模块补默认 1 份，那才是新平台的合理默认。
       *
       * ⚠️ 这里**不重置张数**（那已经由上面的 `outputs: d.outputs` 做了）——
       * 模块勾选只管「出哪几屏」，张数由 `outputs` 管，两件事不合并。
       */
      // 份数表按新规格重新归一（旧平台的模块名在 `moduleCountsOf` 里被丢掉）
      moduleCounts: moduleCountsOf(nextDetail, form.moduleCounts),
    })
  }

  /**
   * 换档位 —— 与换平台同级：整套官方尺寸换掉（970×600 ↔ 1464×600），
   * 模块数上限也不同（基本 5 / 高级 7）。
   * 所以张数必须重算 —— 沿用上一档的张数会出现「高级档第 6 张」这种
   * 基本档根本没有的模块位。
   */
  const switchTier = (t: Tier) => {
    const nextDetail = specFor(profile, t)
    const d = defaultsFor(mode, profile, { detail: nextDetail, withMobile: form.withMobile })
    patch({
      tier: t,
      outputs: d.outputs,
      ratio: mode === 'aplus' ? form.ratio : d.ratio,
      batchSize: fitBatchSize(form.batchSize, profile, nextDetail),
      // 两档的模块清单不同（基本 A+ 5 个 / 高级 A+ 7 个）——
      // 高级档独有那几个模块名在基本档里不存在，必须过一遍交集
      // 份数表按新规格重新归一（旧平台的模块名在 `moduleCountsOf` 里被丢掉）
      moduleCounts: moduleCountsOf(nextDetail, form.moduleCounts),
    })
  }

  /**
   * 移动端资产开关。
   *
   * 只对**官方要求独立移动文件的图位**生效（高级 A+ 的全宽类 1464×600 → 600×450），
   * 小模块（单图/双图/四图）官方是复用桌面图，所以张数按 `slotCount` 逐位累加，
   * 不是简单翻倍。
   */
  const toggleMobile = (on: boolean) => {
    const d = defaultsFor(mode, profile, { detail, withMobile: on })
    patch({ withMobile: on, outputs: d.outputs })
  }

  /**
   * 组装**一个任务**的提交参数。
   *
   * `p` 是产品信息（单个形态 = 页面上那份，批量形态 = 某一行）。
   * 出图参数（模型/张数/比例/分辨率/平台）全部取自页面 —— **不接受按行覆盖**，
   * 这是 CSV 里拒收 `outputs` / `ratio` 这类列的同一个原因：一批图必须同规格，
   * 否则「这一批多少钱」都没法算。
   *
   * 抽成函数是必须的：批量要调 N 次。若把这段内联在 `doGenerate` 里写两遍，
   * 单产品那条路径上的改动（比如新增一个 `ratios` 传递）迟早只改一处。
   */
  const buildArgs = (p: ProductInfo, refImages: string[]): SubmitArgs => {
    const rowPrompts = promptsForProduct(promptCtx, p, refImages.length)
    return {
      appSlug: activeMode.appSlug,
      title: `${profile.name} ${activeName} · ${p.productName}`,
      // 与 jobsFor('detail-page') 同键：合并后全部模式共用一个键，
      // 本页右栏才能一次看全刚提交的活儿
      sourcePage: 'detail-page',
      model: form.model,
      prompt: rowPrompts[0] ?? prompt,
      prompts: rowPrompts,
      // 逐张尺寸：A+ 各模块官方尺寸不同，必须与 prompts 一一对应地传。
      // 只传 aspectRatio 的话，后端 N 个子任务共用一个 size，
      // 970×300 的横幅位会被出成 970×600，铺版时才发现。
      // 用户选了「整批统一尺寸」时，`shots` 里已经是覆盖后的值（见 `shapeOf`），
      // 所以这里不需要第二个分支。
      ...(shots.length ? { ratios: shots.map((s) => s.size) } : {}),
      // 产品信息整块覆盖：`form` 在批量形态下产品字段是空的，
      // 不覆盖就会出现「20 个任务的 inputs 全写着同一个（空）产品名」。
      inputs: {
        ...form,
        __title: activeName,
        platform: profile.id,
        productName: p.productName,
        sellingPoints: p.sellingPoints,
      },
      // 顺序 = 契约：产品图在前、参考版式图在后，提示词里那句「第 1–k 张 =
      // 产品图，第 k+1–k+L 张 = 参考版式图」就是照它写的（见 `refOrder`）。
      // 这里不许自己拼数组 —— 两处各拼一份，改了一处就会让提示词指着版式图
      // 说「这是你的产品」，而出图不报错，只是把别人的货当用户的货还原。
      imageUrls: refOrder(refImages, form.layoutRefs),
      // 整批代表比例（单尺寸时就是它；多尺寸时取第一张，逐张仍以 ratios 为准）
      aspectRatio: shots[0]?.size ?? form.ratio,
      platform: profile.id,
      resolution: batchResolution,
      outputs: batchCount,
      estimatedMs: 45000 * batchCount,
    }
  }

  const doGenerate = () => {
    const common = {
      title: `${profile.name} · ${activeName}`,
      appSlug: activeMode.appSlug,
      model: form.model,
      platform: profile.name,
      aspectRatio: shots[0]?.size ?? form.ratio,
      resolution: batchResolution,
      price: price?.estimatedPrice ?? 0,
      basis: price?.basis,
      free: price?.isFreeThisCall,
    }
    flow.request({
      info: {
        ...common,
        outputs: batchCount,
        refImages: form.refs.length + form.layoutRefs.length,
        prompt,
        /*
         * 逐张提示词 —— **与真正提交的那一份同源**：`prompts` 就是
         * `buildArgs` 里 `promptsForProduct(...)` 的结果，两边读同一个 `soleProduct`。
         *
         * 为什么必须传：`prompt` 那份是**兜底通用词**（`fallbackPrompt`），
         * 而实际发出去的是下面这 N 份逐张词。确认弹窗只显示兜底词的话，
         * 用户在花钱前看到的东西与实际提交的**不是同一份** ——
         * 「预览 == 提交原文」这条在花钱的按钮前面不能破。
         *
         * label 走 `shotLabel(i)`（**一份实现**）：它与「本批出图结构」卡逐行、
         * 与 AI 重写的 brief 用的都是那一个函数 —— 三处各写一遍的话，
         * 「卡片说第 2 张、AI 收到的是第 3 张」而产物通顺、看不出来。
         */
        shotPrompts: prompts.map((p, i) => ({
          label: shotLabel(i),
          prompt: p,
        })),
        /*
         * 逐张提示词的「AI 重写」能力（用户口径「可以修改提示词，还有提示词生成
         * 可以多一个 AI 按钮重置」）。整块 `shotRefine` 由本页拼 —— 上下文
         * （这一张的定位 + 产品名与卖点 + 本平台禁词）只有这一页知道。
         */
        shotRefine,
      },
      args: buildArgs(soleProduct, form.refs),
    })
  }

  /* ------------------------ 页签 1 · 产品信息 ------------------------ */

  /* `detailFillBlocked` 在提示词组装那一段（`copySlots` 下方）—— 「AI 填充」
     与逐张提示词的「AI 重写」共用同一条前置条件，所以只有那一份。 */

  /**
   * 「产品与参考图」卡 —— **页签 1**（2026-10-09 放回左栏第一步）。
   *
   * 沿革（两进两出，别只看一半）：
   *   · 2026-09-29 用户「目标与参数放第一步」→ 它是页签 1；
   *   · 2026-10-08 搬到右栏 `StudioPanel` 的 `head` 槽，
   *     用户口径是「拿之前的参考跟产品放到历史上面」；
   *   · 2026-10-09 用户「产品信息放回 step1，需要 3 step」→ 搬回页签 1。
   *
   * ⚠️ **这不是把 2026-10-08 的判断推翻**，两次说的其实是两件事：
   * 当初把它搬到右栏，对的理由是它**贴着历史好对照**（「我这次用的还是上次那组素材吗」）；
   * 错的是**层级** —— 它是「拿什么去出」，是填表的**第一步**。
   * 放在右栏的后果是：一进页面，视线正中最显眼的位置是「上次出了什么」，
   * 而「这次要出什么」反而要往下找。**第一步的东西必须在第一步。**
   * 所以判据保留（它仍然**进提示词但不进报价**，改多少次都不花钱），只是换了层级。
   *
   * 卡头仍写形态名与说明：形态控件在页签外的顶部条上，但它是这一卡的**主语**
   * （这组素材是给出「电商主图」用的），去掉它这一卡就叫不出名字了。
   * 页签名（`PANES[0].title`）说的是**这一屏在填什么**，卡头说的是**为哪个形态填**，
   * 两者不重复 —— 与 `/professional/*` 的「参考素材 + 服装上身」是同一个排法。
   */
  const stepSource = (
    <Card>
      <CardHead
        title={activeName}
        hint={activeDesc}
        icon="grid"
        right={
          /* 「AI 补卖点」—— 用户只给产品名，卖点由 AI 按平台与形态补齐。
             补的是**文案要点**，不是提示词本身（提示词由 `lib/detailPrompts` 从
             产品信息 + 形态派生，卖点变了它自动跟着重算）。 */
          <AiFillButton<FormState>
            kind="detail"
            fields={FILL_FORM_FIELDS.detail}
            brief={buildDetailFillBrief({
              platform: form.platform,
              mode,
              tier: form.tier,
              productName: form.productName,
              sellingPoints: form.sellingPoints,
              hasRef: form.refs.length > 0,
            })}
            sourcePage="detail-page"
            /* 快照必须在**覆盖前**取 */
            snapshot={{ sellingPoints: form.sellingPoints }}
            onFill={patch}
            blocked={detailFillBlocked}
            label="AI 补卖点"
          />
        }
      />
      <CardBody>
        <Field
          label="产品名称"
          required
          /* 「从商品库选择」——商品资产页录好的档案一次带进来（标题 + 卖点 + 图组）。
             入口挂在产品名这一格，因为它是带入的**入口字段**：带了名字才谈得上
             卖点与图，单独给卖点配一个按钮会让人以为是「只补卖点」。
             与「AI 补卖点」并列而不冲突：一个从自己的库里取，一个让 AI 现写。 */
          right={
            /* 「从商品库选择」—— 按钮 + 弹层一体（全站生成页的统一接入方式）。
               入口挂在产品名这一格，因为它是带入的**入口字段**：带了名字才谈得上
               卖点与图，单独给卖点配一个按钮会让人以为是「只补卖点」。
               与「AI 补卖点」并列而不冲突：一个从自己的库里取，一个让 AI 现写。 */
            <ProductAssetPickerButton onPick={applyProductAsset} />
          }
        >
          <Input
            value={form.productName}
            placeholder="如：华数云硅藻土沥水垫"
            onChange={(e) => patch({ productName: e.target.value })}
          />
        </Field>
        <Field label="卖点 / 文案要点">
          <Textarea
            value={form.sellingPoints}
            rows={3}
            placeholder={'0.5s 吸水、可折叠、耐高温\n（一行一个卖点）'}
            onChange={(e) => patch({ sellingPoints: e.target.value })}
          />
        </Field>

        {/*
          产品图 —— **三个形态都要**（2026-10-07 修）。

          上一版这里是 `mode === 'copy' ? null : <RefUploader …>`：
          「模仿版式」下这个槽位整块消失，于是那一档**唯一的视觉输入就是
          参考版式图**，模型只能照着版式图里那个别人的产品画，或者按
          产品名凭空编一个 —— 用户报障的原话就是「为什么不能选自己的产品的」。

          为什么当时会关掉：那一档的文案写着「参考版式图只提供结构、
          不参与产品还原」，写的人默认「产品由产品名 + 卖点描述」。
          但图生图这条链路上，**文字描述还原不出外观**（颜色/材质/logo 全靠猜），
          而这正是 `refFidelityBlock` 存在的理由：有参考图才会锁外观。
          ⇒ 模仿版式恰恰是**最需要产品图**的一档：一边锁版式、一边锁产品。
        */}
        <RefUploader
          wrap="none"
          label={mode === 'copy' ? '产品图（建议上传）' : '产品图（可选）'}
          fieldHint={
            mode === 'copy'
              ? '你的产品长什么样 —— 只还原它，不用管参考版式图里那个产品'
              : '也可以点上面的「从商品库选择」，标题、卖点与图一起带进来'
          }
          urls={form.refs}
          onChange={(refs) => patch({ refs })}
          max={PRODUCT_REFS_MAX}
          dropLabel="拖拽或点击上传产品图"
          dropHint="支持多选 · 可拖拽排序 · 第一张作为主图"
        />

        {mode === 'copy' ? (
          /*
           * 参考版式图 —— 只服务「模仿版式」（`mode === 'copy'`）。
           *
           * 这里的「从素材库选择」曾经是个真 bug：按钮挂在两个分支外面，
           * 点它永远往 `refs` 写，切到「参考版式」时选中的图根本填不进去。
           * 换成 RefUploader 之后，每个分支各带自己的 onChange，结构上不可能再串。
           *
           * ⚠️ 2026-10-07：**张数上限从 1 放到 6**。用户口径是
           * 「应该是上传长图或者多图，然后出风格、布局结构」。上一版锁死 1 张，
           * 于是「一次给我 6 张竞品详情页、逐张照着出」这件事**没有入口** ——
           * 用户只能一张一张跑，每跑一次重填一次产品名。
           * 逐张与张数的对应关系由 `copyPlanFor` 定义（第 i 张用第 (i-1)%L+1 张），
           * 页面上的结构卡与提示词都从它取，不各算一份。
           *
           * `firstBadge=""`：版式图**没有「主图」这个语义**（那是产品图的概念），
           * 6 张版式是**并列**的、靠序号认，挂个「主图」角标会让人以为只用第一张。
           */
          <RefUploader
            wrap="none"
            label="参考版式图"
            required
            fieldHint="只取结构与信息层级，不参与产品还原 —— 可传一张长图，也可传多张分屏版式"
            urls={form.layoutRefs}
            onChange={(layoutRefs) => patch({ layoutRefs })}
            max={6}
            firstBadge=""
            dropLabel="上传参考版式"
            dropHint="长图或单张版式 · 可多选 · 逐张对应一张产出"
          />
        ) : null}

        {/*
          一整批产品？**去批量工作台** —— 这里只有一条指路的链接，不是一个切换钮。

          为什么这一页不再长出批量形态（2026-09-29 用户口径）：
          原话「批量处理这个需要新页面，这样一来，我可以后补图片，在这个页面继续改不现实」。
          判据落在这两句话上，两条都成立：
            1. **寿命不同**。这一页的状态是 `useState`（刷新即失），而批次要能
               关掉页面、明天回来接着补图、接着改 —— 它是个持久的作业对象。
            2. **输入物相反**（铁律 6 明文允许独立成页的那一条）：这里输入是
               「一个产品的几个字段」，那里输入是「一份表格 + 一堆图」，产出是 N 个任务。
          所以批量形态整块搬到 `/batch`，这里只留一行指路。
          留一个可点的 Segmented 而不搬走的话，就是「同一字段两个入口」——
          而且两处状态不同步（这边改了那边不知道），正是铁律 6 禁的那种。

          ⚠️ 2026-10-08 它跟着这一卡从页签 1 搬来右栏，2026-10-09 又随这一卡搬回去了。
          两次搬家都带着它，理由没变：它讲的是**素材多不多**（一个产品 vs 一整批），
          与「产品图 / 参考图」同域 —— 所以它去哪个页签，取决于那一卡在哪个页签，
          现在就在页签 1 的最底下（整卡的最后一块，不挡上面的必填项）。
        */}
        <div className="batch-hint">
          <Icon name="layers" size={14} />
          <span className="grow">
            有一整批产品要出？去<b>批量出图</b>工作台：上传表格、逐行补图、分批提交，关掉页面回来还在。
          </span>
          <Link className="btn btn--outline btn--sm" to="/batch">
            去批量出图
          </Link>
        </div>
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 3 · 出图参数 ------------------------- */

  /**
   * 「出图参数」卡 —— 2026-09-29 用户原话「出图参数部分放到最后，生成模型放最后」。
   *
   * 上一版这一卡是第一步里的第二张，和产品名 / 卖点 / 参考图挤在同一个页签；
   * 拆出来的判据是**答案来源不同**：形态与产品信息来自买家与商品，
   * 模块顺序 / 比例 / 张数 / 模型来自平台规格与预算 —— 后者天然发生在
   * 参数定稿、临提交之前，所以放最后一步。
   *
   * 卡内顺序也按用户的话调过：**生成模型挪到最末**。它是这批唯一的
   * 「单价乘数」（比例、张数只改数量，模型改单价），放第一位会让人
   * 先挑模型、再回头改张数，两次都要重看报价。
   */
  /* ------------------------- 页签 2 · 出图数量 ------------------------- */

  /**
   * 「出图数量」卡 —— 原「出图参数」里数量那一半（2026-10-08 拆出来）。
   *
   * 为什么拆：产品与参考图搬到右栏之后，原「目标与参数」页签**已经被搬空**
   * （产出形态去了页签外顶部条、素材去了右栏），只剩一个指路链接 —— 留着它
   * 就是一个名叫「目标与参数」的空壳。
   *
   * 而「出图参数」这一张卡里其实是**两个决策域**，判据是「多快会回来改」：
   *   · 出几张、出哪几屏 —— 乘出报价的那一项，用户会反复回调的就是这里；
   *   · 多大、用哪个模型 —— 定稿一次就不再动。
   * 页签的判据就是「按决策域切」（见 `PANES`），那就顺着这个界线分。
   */
  const stepCount = (
    <Card>
      <CardHead
        title="出图数量"
        icon="layers"
        hint={
          mode === 'aplus'
            ? `${detail.tabName} 的模块逐个配张数，合计就是这一批要出的张数`
            : '这一批出几张 —— 数量乘出报价，改一次价格跟着变一次'
        }
      />
      <CardBody>
        {/*
          输出张数 —— 分三副面孔，**同一档里只有一个入口**：

          · 「电商主图」→ **按类型配比**（`ShotMixPicker`）。2026-10-07 用户口径：
            参考竞品那块「自定义配置」（白底图 1 / 场景图 3 / 卖点图 3 / 其他 0，
            最少 7 张），选了「补上，可自由配比」。这一档的张数**就是配比之和**，
            数字输入框必须收起来 —— 留着它就有两个入口写同一个量，
            用户把「场景」加到 3 之后，输入框里还写着 4，提交几张没人说得清。

          · 「平台详情模块」→ **按模块配份数**（`ModulePicker`）。2026-10-08 用户口径：
            「详情跟主图一样可以选择」（配图圈的是「包含模块」那一串）。
            同一条理由：**份数之和 × 每份张数 = 这一批的张数**，所以这一档的
            `CountInput` 同样收起来 —— 改版前它和勾选框并存，两个入口都能写张数。

          · 「模仿版式」→ 仍是**自己敲数字**（2026-09-30 用户口径：
            「可以我们自己输入数量！！」）。这一档没有「类」也没有「官方模块」，
            张数由参考版式图的张数与循环关系决定，给不出比数字框更聪明的控件。

          ⚠️ 「份数」与「张数」不是一个量：官方多图模块（A+ 三图 / 单图 + 侧栏）
          一个模块顶 3 张、2 张，`withMobile` 打开后全宽类还要各加一张。
          ± 调的是份数（这个模块来几轮），界面显示的是张数。混用不会报错，
          只是「选了一个三图模块却只出 1 张」—— 逐张清单里少两行，很难发现。
        */}
        {mode === 'main' ? (
          <Field
            label="各类张数"
            hint={`四类各出几张；某一类填 0 就是这一类不出（合计至少 1 张）。当前共 ${mixTotal(form.mainMix)} 张`}
          >
            <ShotMixPicker mix={form.mainMix} onChange={(m) => patch({ mainMix: m })} />
          </Field>
        ) : mode === 'aplus' ? (
          <Field
            label="包含模块"
            hint={`每个模块出几份（0 = 这个模块不出），合计 ${moduleShotTotal} 张。顺序按 ${detail.tabName} 的官方规格，不跟着点的先后走`}
          >
            <ModulePicker
              spec={detail}
              counts={moduleCounts}
              withMobile={form.withMobile}
              onChange={(next) => patch({ moduleCounts: next })}
            />
          </Field>
        ) : (
          <Field
            label="输出张数"
            hint={`直接输入 1–${MAX_BATCH_COUNT} 之间的数字`}
          >
            <CountInput
              value={form.outputs}
              onChange={(n) => patch({ outputs: n })}
              max={MAX_BATCH_COUNT}
            />
          </Field>
        )}
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 3 · 模型与规格 ------------------------- */

  /**
   * 「模型与规格」卡 —— 原「出图参数」里规格那一半。
   *
   * 卡内顺序按用户的话排过：**生成模型放最末**。它是这批唯一的
   * 「单价乘数」（尺寸、张数只改数量，模型改单价），放第一位会让人
   * 先挑模型、再回头改张数，两次都要重看报价。
   */
  const stepSpec = (
    <Card>
      <CardHead
        title="模型与规格"
        icon="bolt"
        hint={
          mode === 'aplus'
            ? `${profile.name} 官方模块尺寸逐张匹配，一屏回答一个买家问题`
            : '画布尺寸与出图模型 —— 前者决定构图，后者决定单价'
        }
      />
      <CardBody>
            {mode === 'aplus' ? (
              /*
                A+ 这一档的**输出尺寸** —— 2026-09-30 用户口径：
                「这个尺寸有默认，但是可以选择」。

                上一版这里是一块**纯展示**（`.size-list` 里的尺寸 badge 点不动），
                理由是「模块尺寸不是『选一个比例』，给下拉反而让人以为选 16:9 就对」。
                那个理由成立于**默认值**，不成立于**选项本身**：官方尺寸作为默认留着，
                用户想整批改成 1:1 或 3:4 时却只能换平台 —— 所以现在给的是
                「官方尺寸（默认）+ 该平台自己的比例」。

                下面那张逐张清单保留：它回答的是「这一批具体出哪几张、每张多大」，
                改过尺寸之后清单里的数字会**跟着变**（`shots` 是覆盖后的结果），
                所以「改一次全变」看得见，不用靠猜。
              */
              <>
                <Field
                  label="输出尺寸"
                  hint={
                    flatChoices(sizeChoices).length > 1
                      ? '默认按平台官方模块规格逐张匹配；改成某个比例则本批每一张都用它'
                      : `${detail.tabName} 的官方尺寸就是 ${sizeChoices[0]?.options[0]?.label ?? '—'}，没有别的可选`
                  }
                >
                  <GroupedSelect
                    value={form.batchSize}
                    onChange={(v) => patch({ batchSize: v })}
                    groups={sizeChoices}
                    ariaLabel="输出尺寸"
                  />
                </Field>
                <Field
                  label="逐张尺寸"
                  hint={
                    form.batchSize
                      ? `本批统一按 ${sizeLabel(form.batchSize)} 出图；${
                          form.withMobile && mobilePairs(detail).length
                            ? `移动端资产仍按官方 ${mobilePairs(detail).map((p) => sizeLabel(p.mobileSize)).join(' / ')}（官方硬要求，不跟着改）`
                            : `本批共 ${batchCount} 张，逐张按 ${detail.tabName} 官方模块规格（同一模块的第 2 份起标「第 N 轮」）`
                        }`
                      : '按平台官方模块规格逐张匹配，无需手选比例'
                  }
                >
                  <div className="size-list">
                    {shots.map((s, i) => (
                      <div className="size-list__row" key={`${i}-${s.name}`}>
                        <span className="size-list__idx">{i + 1}</span>
                        <span className="grow">{s.name}</span>
                        {s.textFree ? <span className="size-list__tag">无文字</span> : null}
                        <b className="size-list__size">{sizeLabel(s.size)}</b>
                      </div>
                    ))}
                  </div>
                </Field>
              </>
            ) : (
              <Field
                label="比例"
                hint={`${profile.name} 标准：主图 ${profile.sizes.main} / 副图 ${profile.sizes.secondary}`}
              >
                <GroupedSelect
                  value={form.ratio}
                  onChange={(v) => patch({ ratio: v })}
                  groups={ratioChoices}
                  ariaLabel="画面比例"
                />
              </Field>
            )}

            {/*
              输出张数 —— 分两副面孔，**同一档里只有一个入口**：

              · 「电商主图」→ **按类型配比**（`ShotMixPicker`）。2026-10-07 用户口径：
                参考竞品那块「自定义配置」（白底图 1 / 场景图 3 / 卖点图 3 / 其他 0，
                最少 7 张），选了「补上，可自由配比」。这一档的张数**就是配比之和**，
                所以数字输入框必须收起来 —— 留着它就有两个入口写同一个量，
                用户把「场景」加到 3 之后，输入框里还写着 4，提交几张没人说得清。
              · 其余两档 → 仍是**自己敲数字**（2026-09-30 用户口径：
                「可以我们自己输入数量！！」）。上限只剩防误输的 `MAX_BATCH_COUNT`。

              详情档超过图位数**不是错误**：`planDetailShots` 会循环官方模块序列、
              并在逐张尺寸清单里给超出的那些标「第 N 轮」。所以 hint 要说清楚
              「从第几张开始循环、循环出来的怎么认」——用户按下去之前就知道会拿到什么。
            */}
        {/* 生成模型 —— **放最后**（用户点名）。它是这批唯一的「单价乘数」，
            传参不当会让模型按文字自由发挥、无视用户上传的原图。 */}
        <ModelSelect
          kind="image"
          value={form.model}
          onChange={(v) => patch({ model: v })}
          hasRef={form.refs.length > 0 || form.layoutRefs.length > 0}
        />
      </CardBody>
    </Card>
  )

  /* ------------------- 出图参数页签 · 本批逐张结构 ------------------- */

  /**
   * 本批逐张结构。这张卡**不是核对，是产出** —— 它说的是「这一批具体要出哪几张、
   * 每张什么尺寸」，页签 1 与出图参数里都没有这个信息（那里是参数，这里是解构结果）。
   * 与之相对，原来那张「参数核对」卡是把参数复述一遍，已删（见 `PANES` 上方说明）。
   *
   * 清单按形态取：平台详情模块（含移动端资产）看逐屏规划，其余看逐张解构。
   * （原「套图看平台标准结构」那一支随商品套图于 2026-09-30 删除。）
   */
  const structureList = mode === 'copy'
    ? copySlots.map((slot) => ({
        task: `沿用参考版式第 ${slot.layoutNo} 张的结构${
          slot.round > 1 ? `（第 ${slot.round} 轮复用）` : ''
        }`,
        meta: `第 ${slot.no} / ${copySlots.length} 张 · ${sizeLabel(form.ratio)}`,
      }))
    : plannedShots.length
      ? /*
         * 详情页列的是**买家问题**，不是模块名。
         *
         * 上一版这里列的是「详情 · 品牌主视觉」「详情 · 核心痛点」这类模块名 ——
         * 那是内部规格用语，用户看不出这一批到底在讲什么故事；
         * 而且它与用户在「详情页模块顺序」里手写的词对不上号，两边都不知所措。
         * 现在列「买家要问什么 + 这一屏在决策链里的角色」，用户一眼能看出
         * 这 N 屏是不是一条连贯的说服线，缺哪一环也看得出来。
         */
        plannedShots.map((s) => ({
          task: s.question,
          meta: `${BEAT_NAME[s.beat]} · ${sizeLabel(s.size)}${s.fullProduct ? '' : ' · 产品非主体'}`,
        }))
      : shots.map((s) => ({ task: s.task, meta: `${s.name} · ${sizeLabel(s.size)}` }))

  const stepStructure = (
    <>
      <Card>
        <CardHead
          title="本批出图结构"
          hint={
            mode === 'copy'
              ? form.layoutRefs.length > 1
                ? `共 ${structureList.length} 张，逐张沿用参考版式（${form.layoutRefs.length} 张按顺序循环）`
                : `共 ${structureList.length} 张，沿用参考版式结构`
              : plannedShots.length
                ? `共 ${structureList.length} 屏，一屏回答一个买家问题 —— 合起来是一篇连贯的说服页`
                : `按下面顺序生成 ${structureList.length} 张，尺寸逐张匹配`
          }
          icon="grid"
        />
        <CardBody tight>
          {structureList.length ? (
            <ol className="structure">
              {structureList.map((s, i) => (
                <li key={`${i}-${s.task}`} className="structure__item">
                  <span className="structure__idx">{i + 1}</span>
                  <span className="grow">{s.task}</span>
                  {s.meta ? <span className="structure__size">{s.meta}</span> : null}
                </li>
              ))}
            </ol>
          ) : (
            /*
             * 空态必须给一句话。上一版这里是直接渲染空 `<ol>` —— 卡片框在、标题在、
             * 右上角还挂着 badge，里面什么都没有，看起来像加载失败。
             */
            <div className="text-xs muted" style={{ padding: '12px 14px 16px', lineHeight: 1.7 }}>
              填好产品名称与卖点后，这里会列出这一批要出的每一张。
            </div>
          )}
          {mode === 'copy' ? (
            /*
             * 模仿版式的收口说明。**留在卡片里**而不是塞进 hint：
             * 这一档最容易被误解成「照着参考图重画一张」——
             * 用户上一轮报障就是「为什么不能选自己的产品」（见页签① 产品图那段）。
             */
            <div className="text-xs muted" style={{ padding: '10px 14px 14px', lineHeight: 1.7 }}>
              模仿版式不打散结构：整张图沿用参考版式的排版，只把产品与文案换成你的 ——
              产品外观以上传的**产品图**为准，参考版式图只提供结构与信息层级。
            </div>
          ) : null}
        </CardBody>
      </Card>
    </>
  )

  /**
   * 两个页签的内容。**单行写，分支数 == 页签数** —— `smoke-detail-specs` 与
   * `smoke-nav` 都会数这里的逗号，拆行就数不出来。
   *
   * ⚠️ 分支顺序**必须与 `PANES` 逐项对齐**：第 2 项是「出图参数」，
   * 它由两块组成 —— 参数卡 + 本批逐张结构卡。结构卡是从已删的「核对」页签搬过来的，
   * 搬过来而不是一起删掉，是因为它讲的是解构结果、不是参数复述（详见 `structureList`）。
   * 页签数比分支数多时，点多的那个页签会取到 `undefined`：
   * 页签照常高亮、内容一片空白，**typecheck 也查不出来**（数组下标越界不是类型错误）。
   * 冒烟里的「页签数 == 分支数」就是拦这个的。
   *
   * 上一版这里还有第 3 个分支（派生页签复用这三块，靠 `isDerive` 内部变体），
   * 派生下架后同一份 JSX 不再需要「一物两用」—— 这也是这次改动最主要的收益。
   */
  // prettier-ignore
  const stepBody = [stepSource, stepCount, <>{stepSpec}{stepStructure}</>][Math.min(step, PANES.length - 1)]

  /* ------------------------------ 常驻提交栏 ------------------------------ */

  const submitLabel = `生成${activeName}`

  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          price={price?.estimatedPrice ?? 0}
          basis={price?.basis}
          free={price?.isFreeThisCall}
          disabled={!canSubmit}
          disabledReason={missingReason}
          label={submitLabel}
          onGenerate={doGenerate}
        />
      </CardBody>
    </Card>
  )

  return (
    <div className="page page--wide">
      {/*
        这里原本是一行**页签外的一级 chip-row**（电商主图 / 详情页模块）。
        2026-09-30 整块删除 —— 用户原话「详情页跟主图合并为一页，
        然后在输入的时候选择就可以」：

          · 它与页签 1 的「产出形态」控件做的是同一件事（挑一种产出），
            却分在两层，用户要先做 2 选 1 才看得到完整的形态清单；
          · 那两个选项不承载任何别的信息（平台 / 张数 / 比例 / 必填项都与它无关），
            所以它带来的只有「多点一次」。

        现在四个形态平铺在**页签外的顶部条**上那颗「产出形态」下拉里
        （2026-10-08 从 `stepSource` 卡里提上去的，见 `studioModes.ts`）。
        ⚠️ 别再用「Chips + 分组」把它加回来：形态之间是**并列**的四个选项，
        不是「先选方向再选形态」的两级；真要再分组，先回来读这段。
      */}

      {/*
        平台条 —— 全页平台入口。
        它比形态更上游：换平台等于换整套规格（模块尺寸 / 比例 / 图内文字语言 /
        卖点框架 / 禁词），所以不能藏在某个页签里。

        （上一版这里随页签换语义：出图页签叫「投放平台」、派生页签叫「原图来自」，
        靠的是两个独立字段。派生下架后全页只有这一个平台字段，一个语义。）
      */}
      <div className="platform-bar">
        <span className="platform-bar__label">投放平台</span>
        <Select
          value={form.platform}
          onChange={(e) => switchPlatform(e.target.value)}
          aria-label="投放平台"
        >
          {PLATFORM_GROUPS.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
        {/*
          产出形态 —— 2026-10-08 从页签 1 移到**页签外**（用户看图圈了「产出形态」
          与顶部这一行，箭头指向这里）。

          为什么它属于页签外：形态决定**下面所有的槽位**（主图四类的配比、详情模块
          清单、模仿版式的版式图槽位、张数体系），换一次等于换一整套默认值
          （见 `switchMode`）—— 与「投放平台」同级，是本项目界定的根决策。
          留在页签 1 里，用户切到「模型与规格」就换不了形态了。

          ⚠️ 这不是「把 2026-09-30 删掉的那排 chip 加回来」：当时删的是一排
          平铺的 chip（3 个形状各占一格），现在给的是一个**下拉** ——
          与 `/professional/*`、`/social-image` 那三处页签外根决策条同一形态
          （§8.35）。形态再多也不会把顶部条撑高。

          ⚠️ 全页仍然**只有这一个形态入口**。页签 1 里那一份已经删除，
          两处并存就会出现「页签外的下拉写着详情、页签里的分段器还亮着主图」。
        */}
        <span className="platform-bar__label">产出形态</span>
        <Select
          value={mode}
          onChange={(e) => switchMode(e.target.value as StudioModeId)}
          aria-label="产出形态"
        >
          {STUDIO_MODES.map((m) => (
            <option key={m.id} value={m.id}>
              {modeName(m.id)}
            </option>
          ))}
        </Select>
        <span className="platform-bar__meta">
          主图 {profile.sizes.main} / 副图 {profile.sizes.secondary} · 图内文字 {profile.textLanguage}
          {profile.textInImage ? '' : '（该平台主图不许出现文字）'}
        </span>
      </div>

      {/*
        规格条 —— 在平台之下、页签之上，且**只在「平台详情模块」模式下出现**。
        为什么不塞进「出图参数」卡：档位不是可调参数，是**换一套规格真源**
        （模块数上限 5↔7、全宽尺寸 970×600↔1464×600，两档比例不通用），
        与「投放平台」同级，属于本项目界定的根决策 → 必须放页签外。
        为什么只在 aplus 模式出现：档位与移动端资产只影响这一档的出图，
        别的模式下它俩改不了任何一张图，摆着就是噪音。
      */}
      {mode === 'aplus' && (profile.detailPremium || mobilePairs(detail).length > 0) ? (
        <div className="platform-bar">
          {profile.detailPremium ? (
            <>
              <span className="platform-bar__label">A+ 档位</span>
              <Segmented
                value={form.tier}
                onChange={(v) => switchTier(v as Tier)}
                options={[
                  { value: 'basic', label: '基本 A+' },
                  { value: 'premium', label: '高级 A+' },
                ]}
              />
            </>
          ) : null}
          {mobilePairs(detail).length > 0 ? (
            <>
              <span className="platform-bar__label">移动端资产</span>
              <Segmented
                value={form.withMobile ? 'on' : 'off'}
                onChange={(v) => toggleMobile(v === 'on')}
                options={[
                  { value: 'off', label: '不生成' },
                  { value: 'on', label: `生成 +${mobilePairs(detail).length} 张` },
                ]}
              />
            </>
          ) : null}
          <span className="platform-bar__meta">
            {detail.tabName} · {detail.modules.length} 个模块 / {slotCount(detail, form.withMobile)} 张 ·{' '}
            {detailSizes(detail).length} 种官方尺寸
            {detail.tier === 'premium'
              ? '（全宽 1464×600，移动版要独立出图 600×450）'
              : detail.tier === 'basic'
                ? '（全宽 970×600，不提供独立移动图）'
                : ''}
          </span>
        </div>
      ) : null}

      <GeneratorFrame
        form={
          <WorkflowTabs tabs={PANES} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              activeHint={`本批 ${batchCount} 张，单张约 45 秒，可以先去别的页面干活`}
              resultTitle={`${activeName} 结果`}
              /* 历史与结果同源收窄（见上面 `jobsFor('detail-page', activeMode.appSlug)`），
                 标题跟着当前形态走 —— 否则卡片里全是本形态的记录，标题却写「本页」。 */
              historyTitle={`${activeName} 历史`}
              resultEmpty={`当前形态：${profile.name} · ${activeName}。确认参数后点「${submitLabel}」。`}
              aspectRatio={sizesInBatch.length === 1 ? sizesInBatch[0] : shots.length ? undefined : form.ratio}
            />
          </>
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}
